import { overlay } from "overlay-kit";
import { FC, type MouseEvent, useState } from "react";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { PageCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { Dialog } from "mds/components/Dialog";
import { ListItem } from "mds/components/ListItem";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAppSelector } from "store";
import {
  selectCurrentCourseId,
  selectCurrentSectionId,
  selectCurrentTermId,
  selectSectionsByTermAndCourse,
} from "store/selectors";

const COPY_TO_SECTION_MODAL_TITLE_KEYS = {
  activity_page: "copy_to_section_modal.title.activity_page",
  assignment: "copy_to_section_modal.title.assignment",
  course_resource: "copy_to_section_modal.title.course_resource",
  instructor_workspace: "copy_to_section_modal.title.instructor_workspace",
} as const satisfies Record<PageCategory, string>;

const COPY_TO_SECTION_MODAL_COPY_BUTTON_KEYS = {
  activity_page: "copy_to_section_modal.copy_button.activity_page",
  assignment: "copy_to_section_modal.copy_button.assignment",
  course_resource: "copy_to_section_modal.copy_button.course_resource",
  instructor_workspace: "copy_to_section_modal.copy_button.instructor_workspace",
} as const satisfies Record<PageCategory, string>;

type ShowCopyToSectionModalOptions = {
  category: PageCategory;
  onConfirm: (sectionIds: string[]) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showCopyToSectionModal = ({ category, onConfirm }: ShowCopyToSectionModalOptions) =>
  overlay.open(({ isOpen, unmount }) => (
    <Dialog
      dialogPanelClassName="w-[560px]"
      open={isOpen}
      title={t(COPY_TO_SECTION_MODAL_TITLE_KEYS[category])}
      onClose={unmount}
    >
      <CopyToSectionModal
        category={category}
        onClose={unmount}
        onConfirm={(sectionIds) => {
          onConfirm(sectionIds);
          unmount();
        }}
      />
    </Dialog>
  ));

export type CopyToSectionModalProps = {
  category: PageCategory;
  onClose: () => void;
  onConfirm: (sectionIds: string[]) => void;
};

export const CopyToSectionModal: FC<CopyToSectionModalProps> = ({
  category,
  onClose,
  onConfirm,
}) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const currentTermId = useAppSelector(selectCurrentTermId);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(currentCourseId);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(currentTermId);
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);

  const sections = useAppSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, selectedCourseId),
  );

  const eligibleSections = sections.filter((section) => section.id !== currentSectionId);
  const selectedSectionIdSet = new Set(selectedSectionIds);
  const allSelected =
    eligibleSections.length > 0 && eligibleSections.length === selectedSectionIds.length;
  const copyButtonLabel = t(COPY_TO_SECTION_MODAL_COPY_BUTTON_KEYS[category]);

  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
    setSelectedSectionIds([]);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
    setSelectedSectionIds([]);
  };

  const handleSectionSelected = (event: MouseEvent, sectionId: string) => {
    event.preventDefault();
    event.stopPropagation();

    toggleSectionChecked(sectionId);
  };

  const toggleSectionChecked = (sectionId: string) => {
    const newSelectedSectionIds = [...selectedSectionIds];

    const sectionIndex = newSelectedSectionIds.indexOf(sectionId);
    if (sectionIndex > -1) {
      newSelectedSectionIds.splice(sectionIndex, 1);
    } else {
      newSelectedSectionIds.push(sectionId);
    }

    setSelectedSectionIds(newSelectedSectionIds);
  };

  const onSelectAll = () => {
    setSelectedSectionIds(allSelected ? [] : eligibleSections.map((section) => section.id));
  };

  const handleSubmit = () => {
    if (selectedSectionIds.length === 0) return;
    onConfirm(selectedSectionIds);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <label className="min-w-0 max-w-[280px]">
            <span className="sr-only">{t("home.courses.select_course_label")}</span>

            <CourseMenu
              className="max-w-full"
              selectedCourseId={selectedCourseId}
              size="s"
              onCourseChange={handleCourseChange}
            />
          </label>

          <label className="shrink-0">
            <span className="sr-only">{t("home.terms.select_term_label")}</span>

            <TermMenu selectedTermId={selectedTermId} size="s" onTermChange={handleTermChange} />
          </label>
        </div>

        <LinkButton className="shrink-0" size="s" onClick={onSelectAll}>
          {t(allSelected ? "common.unselect_all" : "common.select_all")}
        </LinkButton>
      </div>

      <div className="mb-2 mt-4 max-h-[240px] overflow-y-auto rounded-lg border border-black-tint-90 p-2">
        {eligibleSections.length === 0 ? (
          <div className="text-black-tint-70">{t("copy_to_section_sub_menu.no_sections")}</div>
        ) : (
          eligibleSections.map((section) => (
            <div key={section.id}>
              <ListItem
                className="gap-2"
                label={section.title}
                onClick={() => toggleSectionChecked(section.id)}
              >
                <Checkbox
                  checked={selectedSectionIdSet.has(section.id)}
                  onContainerClick={(e) => handleSectionSelected(e, section.id)}
                >
                  <span>{section.title}</span>
                </Checkbox>
              </ListItem>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 text-black-tint-70">
        {t("copy_to_section_modal.selected_count", { count: selectedSectionIds.length })}
      </div>

      <div className="mt-8 flex justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={selectedSectionIds.length === 0}
          kind="accent"
          size="m"
          title={copyButtonLabel}
          onClick={handleSubmit}
        >
          {copyButtonLabel}
        </SquareButton>
      </div>
    </div>
  );
};
